import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import CalendarIcon from '@atlaskit/icon/core/calendar';
import { fg } from '@atlaskit/platform-feature-flags';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	useIsConfigLoaded,
} from '../../../../../controllers/store/bootstrap';
import { filter as calendarTypeFilter } from '../../../../../controllers/store/search/filters/calendar-type';

import { i18n } from './messages';

const CalendarTypeFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: calendarTypeFilter.id,
	});
	const shouldEagerLoadOptions = fg('rovo-fps-3p-ux-salesforce-calendar-type-filter');
	const isConfigLoaded = useIsConfigLoaded();

	// Hold the "did mount" flip until isConfigLoaded is true so the bootstrap-config race
	// cannot leave the filter permanently empty.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!shouldEagerLoadOptions || hasLoadedInitialOptions.current) {
			return;
		}
		if (!isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldEagerLoadOptions, isConfigLoaded]);

	// Hide the filter entirely when the feature gate is off — matching the
	// SalesforceStageFilter pattern. Without this guard the filter would render
	// (empty) because its store `products` list always contains Salesforce.
	if (!filterData || !shouldEagerLoadOptions) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={calendarTypeFilter.id}
			label={formatMessage(i18n.calendarTypeFilterLabel)}
			noOptionsMessage={formatMessage(i18n.calendarTypeFilterNoOptions)}
			iconBefore={<CalendarIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => {
				return setOption(option);
			}}
			clearOptions={() => {
				return clearOptions();
			}}
		/>
	);
};

export default CalendarTypeFilter;
